// How your data is kept (F500, decision 110; PRD 01 RF-71): the privacy
// notice, readable without a session, linked from Before you fly and from
// the footer.

import { Link, createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/privacy")({
  component: Privacy,
});

function Privacy() {
  return (
    <div className="mx-auto max-w-3xl space-y-8 pt-4" data-testid="privacy-notice">
      <header className="space-y-2">
        <h1 className="page-title">How your data is kept</h1>
        <p className="muted">Plain words on what ShariFly keeps of you and of the aircraft you fly, who sees it, and how to take it away.</p>
      </header>
      <Part title="What the platform keeps">
        <ul className="list-disc space-y-1 pl-5">
          <li>Your profile: the Discord account you sign in with (its id, its name, its picture, the e-mail it shared when it did), the first and last name you give, your handle.</li>
          <li>Your choices: what the other pilots see of you, whether you receive e-mails, whether your name goes with your seat in a shared cockpit.</li>
          <li>Your memberships, your requests to join, the invites you use, the aircraft you own.</li>
          <li>Your bookings and the sessions of the app on your PC: when you took an aircraft and when you returned it, and the position it published while you flew.</li>
          <li>The flights and the events your aircraft reported: the recorder&apos;s track, the landing, the limits, the figures of the systems, the entries of the technical log.</li>
          <li>
            The aircraft&apos;s own state files, as the add-on writes them, copied whole at every return. They hold what the aircraft holds: the FMS flight log, the ACARS messages written in
            flight, the settings of the avionics. They are kept so the next pilot gets the aircraft as you left it.
          </li>
          <li>The log of what happened on each aircraft, append-only: who booked, who returned, who joined, who restored an entry.</li>
          <li>The errors the app and the pages met, as they were shown to you: what failed, the app&apos;s version and your system; never your files, and no path with your name in it.</li>
        </ul>
      </Part>
      <Part title="Who sees what">
        <p>
          The members of the aircraft you share see you as you chose: your name or your handle alone, every member or the owner and the captains only. The owner of an aircraft is always named
          as its owner. The administrators of the platform see everyone; they run it. A visitor without a session sees nothing of any aircraft or pilot. The state files are never shown to anyone;
          they move between the platform and the PC of the pilot who has the aircraft.
        </p>
      </Part>
      <Part title="Where and for how long">
        <p>
          The platform runs on servers in the European Union. Your data stays while your account exists. When you delete the account, your name, handle, e-mail and Discord link are removed at
          once; the history of the aircraft you flew stays whole, attributed to a removed pilot, because it belongs to the aircraft and to the pilots who still fly it.
        </p>
      </Part>
      <Part title="What you can do">
        <p>
          Change what others see of you under Account, Privacy. Delete the account under Account, Leave the platform. Ask the administrators for an export of what is kept of you, from Help. No
          ownership is claimed over the states you upload: they are yours and your aircraft&apos;s.
        </p>
      </Part>
      <Part title="Who runs it">
        <p>ShariFly is run by its author for a circle of simulator pilots, free during the beta. It is not affiliated with the makers of the aircraft add-ons or of the simulator.</p>
      </Part>
      <p className="muted text-sm">
        <Link to="/" className="underline">
          Back to the start
        </Link>
      </p>
    </div>
  );
}

function Part({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <h2 className="text-[1.1429rem] font-semibold">{title}</h2>
      <div className="space-y-2 text-[0.9286rem] text-ink-2">{children}</div>
    </section>
  );
}
